import { z } from "zod";
import { ProjectStatusEnum, ProjectWorkKindSchema } from "./projects.js";
/**
 * Portal projects — read-only schemas for the customer-facing
 * project plan view. Subset of the staff project schemas (no
 * stakeholders/team — PII; no budget — commercial; no kanban
 * config — staff-only structure; no `createdBy` — internal
 * identity leak).
 *
 * Canon: docs/design-canon/04-shared/ssot.md.
 */
// ─── Project ───────────────────────────────────────────────
/**
 * Read-only project projection visible to portal users when
 * the project's `sharedWithPortal` is true.
 */
export const PortalProjectSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    workKind: ProjectWorkKindSchema.default("project"),
    // Description is high-level project narrative; staff control its
    // contents and can leave it blank. Surfaced verbatim — staff are
    // expected to draft it knowing the customer will read it.
    description: z.string().nullable(),
    status: ProjectStatusEnum,
    startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
});
// ─── Milestone ─────────────────────────────────────────────
/**
 * Milestone = a task with `milestone='true'`. Surfaced separately
 * from regular tasks because milestones are the natural read-only
 * timeline view for an external stakeholder.
 *
 * Customer never sees: assignee, priority, hour estimates, status
 * comments, dependencies (those are operational — staff context).
 * They DO see title, dates, completed-at — the "is it done yet"
 * shape that matters to a counterparty.
 */
export const PortalMilestoneSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    completedAt: z.string().datetime().nullable(),
});
// ─── Responses ─────────────────────────────────────────────
export const PortalProjectListResponseSchema = z.object({
    projects: z.array(PortalProjectSchema),
});
export const PortalProjectDetailResponseSchema = z.object({
    project: PortalProjectSchema,
    milestones: z.array(PortalMilestoneSchema),
});
//# sourceMappingURL=portal-projects.js.map